import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';

export const BENCHMARK_VERDICTS = ['pass', 'partial', 'fail', 'na'] as const;
export const BenchmarkVerdictSchema = z.enum(BENCHMARK_VERDICTS);
export type BenchmarkVerdict = z.infer<typeof BenchmarkVerdictSchema>;

export const BENCHMARK_MODES = ['real', 'mock'] as const;
export const BenchmarkModeSchema = z.enum(BENCHMARK_MODES);
export type BenchmarkMode = z.infer<typeof BenchmarkModeSchema>;

export const BenchmarkTranscriptTurnSchema = z.object({
  role: z.string().min(1).max(32),
  text: z.string().max(20000),
});
export type BenchmarkTranscriptTurn = z.infer<typeof BenchmarkTranscriptTurnSchema>;

// --- Ingest (from the external runner; auth = AI token) ---

export const BenchmarkConversationIngestSchema = z.object({
  scenarioName: z.string().min(1).max(128),
  persona: z.string().max(2000).optional(),
  verdict: BenchmarkVerdictSchema,
  score: z.number().int().min(0).max(100).default(0),
  reasoning: z.string().max(4000).optional(),
  issues: z.array(z.string().max(2000)).max(50).default([]),
  outcome: z.string().max(64).default('unknown'),
  expectedOutcome: z.string().max(64).optional(),
  turns: z.number().int().nonnegative().default(0),
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  productCostUsd: z.number().nonnegative().default(0),
  transcript: z.array(BenchmarkTranscriptTurnSchema).max(200).default([]),
});
export type BenchmarkConversationIngest = z.infer<typeof BenchmarkConversationIngestSchema>;

export const BenchmarkRunIngestSchema = z.object({
  label: z.string().max(200).optional(),
  mode: BenchmarkModeSchema.default('real'),
  agentModel: z.string().min(1).max(128),
  aborted: z.boolean().optional(),
  notes: z.string().max(2000).optional(),
  testCostUsd: z.number().nonnegative().default(0),
  metadata: z.record(z.unknown()).optional(),
  conversations: z.array(BenchmarkConversationIngestSchema).min(1).max(500),
});
export type BenchmarkRunIngest = z.infer<typeof BenchmarkRunIngestSchema>;

export const BenchmarkRunIngestResponseSchema = z.object({
  id: UuidSchema,
});
export type BenchmarkRunIngestResponse = z.infer<typeof BenchmarkRunIngestResponseSchema>;

// --- Read (admin panel) ---

export const BenchmarkRunSummarySchema = z.object({
  id: UuidSchema,
  applicationId: UuidSchema,
  applicationName: z.string().nullable(),
  label: z.string().nullable(),
  mode: BenchmarkModeSchema,
  agentModel: z.string(),
  aborted: z.boolean(),
  scenarioCount: z.number().int().nonnegative(),
  passCount: z.number().int().nonnegative(),
  partialCount: z.number().int().nonnegative(),
  failCount: z.number().int().nonnegative(),
  avgInputTokensPerTurn: z.number().int().nonnegative(),
  avgOutputTokensPerTurn: z.number().int().nonnegative(),
  productCostUsd: z.number().nonnegative(),
  testCostUsd: z.number().nonnegative(),
  costPerConversationUsd: z.number().nonnegative(),
  notes: z.string().nullable(),
  createdAt: IsoTimestampSchema,
});
export type BenchmarkRunSummary = z.infer<typeof BenchmarkRunSummarySchema>;

export const BenchmarkConversationSchema = z.object({
  id: UuidSchema,
  scenarioName: z.string(),
  persona: z.string().nullable(),
  verdict: BenchmarkVerdictSchema,
  score: z.number().int(),
  reasoning: z.string().nullable(),
  issues: z.array(z.string()),
  outcome: z.string(),
  expectedOutcome: z.string().nullable(),
  turns: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  productCostUsd: z.number().nonnegative(),
  transcript: z.array(BenchmarkTranscriptTurnSchema),
});
export type BenchmarkConversation = z.infer<typeof BenchmarkConversationSchema>;

export const BenchmarkRunDetailSchema = BenchmarkRunSummarySchema.extend({
  metadata: z.record(z.unknown()),
  conversations: z.array(BenchmarkConversationSchema),
  // Fixed infrastructure cost context for the run's application: Railway spend
  // over the last 30 days. NOT a per-conversation cost — it's amortized over the
  // month's whole volume. null when the app has no linked Railway project.
  railwayLast30dUsd: z.number().nonnegative().nullable(),
});
export type BenchmarkRunDetail = z.infer<typeof BenchmarkRunDetailSchema>;

export const BenchmarkRunListResponseSchema = z.object({
  items: z.array(BenchmarkRunSummarySchema),
});
export type BenchmarkRunListResponse = z.infer<typeof BenchmarkRunListResponseSchema>;
